import { css } from '@emotion/react'
import {
  DesignToken,
  RadiusToken,
} from '@livechat/design-system-react-components'
import { useMemo } from 'react'

import type { IApplicationMediaType } from '@/apis/apps/interfaces'
import type { MediaSlide } from '@/components/Carousel/interfaces'
import { Carousel } from '@/components/Carousel/Carousel'
import { CarouselItem } from '@/components/Carousel/CarouselItem'
import { VideoPlayer } from '@/components/VideoPlayer'
import { CAROUSEL_TYPES } from '@/constants'
import { LocalImage, type ProcessedImage } from '../LocalImage'

const borderCss = css`
  border: 1px solid var(${DesignToken.BorderBasicPrimary});
  border-radius: var(${RadiusToken.Radius1});
`

const playerCss = css`
  width: 100%;
  height: 100%;
  margin: 0;
  object-fit: cover;

  .react-player__preview {
    ${borderCss}
    pointer-events: none;
  }
`

const imageCss = css`
  .gatsby-image-wrapper {
    width: 100%;
    height: 100%;
    ${borderCss}
  }

  > img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    ${borderCss}
  }
`

const boxCss = css`
  position: relative;
  width: 100%;

  &:after {
    content: '';
    display: block;
    padding-bottom: 56.25%;
  }
`

const boxContentCss = css`
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
`

const carouselItemCss = css`
  min-width: 66%;
  width: unset;
`

const itemSnapAlignCss = css`
  scroll-snap-align: center;
`

type GalleryImage = IApplicationMediaType & { localImage: ProcessedImage }

const getGallerySlides = (
  images: GalleryImage[],
  videoUrl?: string
): MediaSlide[] => {
  const videoSlide = videoUrl
    ? [
        {
          url: videoUrl,
          component: (
            <div css={boxCss}>
              <div css={[boxContentCss, playerCss]}>
                <VideoPlayer
                  url={videoUrl}
                  width="100%"
                  height="100%"
                  controls={false}
                  light
                />
              </div>
            </div>
          ),
          type: CAROUSEL_TYPES.VIDEO,
          id: videoUrl,
        },
      ]
    : []

  const imageSlides = images?.map((image) => ({
    ...image,
    component: (
      <div css={boxCss}>
        <div css={[boxContentCss, imageCss]}>
          <LocalImage
            image={image.localImage}
            fallbackUrl={image?.large || image?.original}
            alt={image.title}
          />
        </div>
      </div>
    ),
    type: CAROUSEL_TYPES.IMAGE,
  }))

  return [...videoSlide, ...imageSlides]
}

interface Props {
  images: GalleryImage[]
  videoUrl?: string
}

const Gallery = ({ images = [], videoUrl }: Props) => {
  const slides = useMemo(
    () => getGallerySlides(images, videoUrl),
    [images, videoUrl]
  )

  return (
    <Carousel
      preview
      items={slides}
      renderItem={({ item, isSnapPoint, handleClick, preview }) => (
        <CarouselItem
          key={item.id}
          isSnapPoint={isSnapPoint}
          onClick={handleClick}
          preview={preview}
          css={[carouselItemCss, isSnapPoint && itemSnapAlignCss]}
        >
          <div>{item.component}</div>
        </CarouselItem>
      )}
    />
  )
}

export default Gallery
